import escapeHtml from 'lodash-es/escape.js';

/**
 * Every http(s) URL in a block of text. Trailing punctuation is left out so a link at the end of a
 * sentence still parses.
 */
const URL_PATTERN = /https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]}]/g;

/**
 * Escapes text for a tooltip's HTML string. dw-tooltip renders tooltips with `allowHTML`, and
 * titles, names and emails are user text, so every value goes through this before it is
 * interpolated.
 */
export { escapeHtml };

/** `url` when it is an http(s) link, else null: nothing else may become a tooltip `href`. */
export const safeHttpUrl = (url) => {
  try {
    const { protocol, href } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? href : null;
  } catch {
    return null;
  }
};

/** Gap between a chip and its tip, so the tip sits clear of the chip with its arrow on the chip's edge (design notes: offset 8). */
export const CHIP_TIP_OFFSET = [0, 8];

/** CHIP_TIP_OFFSET for a tip anchored on the chip's text, which sits 3px (padding + border) inside the chip's edge. */
export const CHIP_TEXT_TIP_OFFSET = [0, CHIP_TIP_OFFSET[1] + 3];


/** Reference type → its chip element. Every other chip list derives from this. */
export const CHIP_TAG_BY_TYPE = {
  CARD: 'reference-card-chip',
  BOARD: 'reference-board-chip',
  SUBTASK: 'reference-subtask-chip',
  TAG: 'reference-tag-chip',
  USER: 'reference-user-chip',
  FILE: 'reference-attachment-chip',
  AICHAT: 'reference-aichat-chip',
};

/** A deleted object's chip icon: the attachments' broken card and board icons, `link_off` for the rest. */
export const deletedChipIcon = (type) => ({ CARD: 'reference-card-off', BOARD: 'reference-board-off' })[type] || 'link_off';

/** An AI Helper upload is written `<a data-type="attachment">` and shows as the file chip. */
export const referenceTypeOf = (dataType) => (dataType === 'attachment' ? 'FILE' : String(dataType || '').toUpperCase());

/** Every `data-type` an editor draws as a reference chip. */
export const CHIP_DATA_TYPES = [...Object.keys(CHIP_TAG_BY_TYPE).map((type) => type.toLowerCase()), 'attachment'];

/**
 * Whether `path` is an in-app board or card page (`/acc_…/board/brd_…[/crd_…]`), optionally with
 * `?tab=tasks|attachments` or an AI chat (`?aichat=aic_…` / `?card-aichat=aic_…`): what a chip's tip link may open.
 */
export const isReferencePagePath = (path) =>
  /^\/acc_[A-Za-z0-9]+\/board\/brd_[A-Za-z0-9]+(\/crd_[A-Za-z0-9]+)?(\?(tab=(tasks|attachments)|(card-)?aichat=aic_[A-Za-z0-9]+))?$/.test(path || '');

/** The AI Helper's old chip elements, which old prompts and drafts still hold. */
const OLD_CHIP_TAG_BY_TYPE = {
  CARD: 'card-chip-item',
  BOARD: 'board-chip-item',
  SUBTASK: 'subtask-chip-item',
  TAG: 'tag-chip-item',
  USER: 'user-chip-item',
  FILE: 'attachment-chip-item',
  AICHAT: 'aichat-chip-item',
};

/** Every old chip element a prompt can hold, including the legacy `chip-item` uploads, for `chipsToAnchors`. */
export const CHIP_SELECTOR = [...Object.values(OLD_CHIP_TAG_BY_TYPE), 'chip-item'].join(', ');

/** Legacy `chip-item`: its `item-type` → reference type. */
const TYPE_BY_LEGACY_ITEM_TYPE = { USER: 'USER', BOARD: 'BOARD', CARD: 'CARD', TASK: 'SUBTASK', SUBTASK: 'SUBTASK', TAG: 'TAG', FILE: 'FILE', ATTACHMENT: 'FILE' };

/** The reference type a chip element stands for, or undefined. */
export const chipReferenceType = (chip) => {
  const tag = chip.tagName.toLowerCase();
  if (tag === 'chip-item') {
    return TYPE_BY_LEGACY_ITEM_TYPE[(chip.getAttribute('item-type') || 'FILE').toUpperCase()];
  }
  return Object.keys(OLD_CHIP_TAG_BY_TYPE).find((type) => OLD_CHIP_TAG_BY_TYPE[type] === tag)
    || Object.keys(CHIP_TAG_BY_TYPE).find((type) => CHIP_TAG_BY_TYPE[type] === tag);
};

/**
 * A chip tip's first lines: the type icon, the title and, when known, the path that opens the
 * reference. Each path item (`{ label, navigateUrl }`) is a link when it has a `navigateUrl`, else plain text;
 * `titleNavigateUrl` makes the title a link. Every value escaped — the tip is rendered with `allowHTML`.
 */
export const chipTipHeader = ({ icon, iconFont, title, pathItems = [], titleNavigateUrl }) => {
  const pathStyle = 'color:rgba(255,255,255,0.7);font-size:11px;line-height:14px;overflow-wrap:anywhere;';
  const link = (label, navigateUrl, style) =>
    navigateUrl
      ? `<span data-navigate-url="${escapeHtml(navigateUrl)}" style="${style}cursor:pointer;">${escapeHtml(label)}</span>`
      : `<span style="${style}">${escapeHtml(label)}</span>`;
  const items = pathItems.filter((item) => item.label);
  const pathHTML = items.length
    ? `<div style="${pathStyle}">${items.map((item) => link(item.label, item.navigateUrl, pathStyle)).join(' / ')}</div>`
    : '';
  return `<div style="display:flex;align-items:flex-start;gap:6px;">
      <kerika-icon name="${escapeHtml(icon)}"${iconFont ? ` iconfont="${escapeHtml(iconFont)}"` : ''} size="16" style="--dw-icon-color:#fff;flex-shrink:0;"></kerika-icon>
      <div>
        <div style="color:#fff;font-size:12px;line-height:16px;overflow-wrap:anywhere;">${link(title, titleNavigateUrl, 'color:#fff;font-size:12px;line-height:16px;overflow-wrap:anywhere;')}</div>
        ${pathHTML}
      </div>
    </div>`;
};

/**
 * An in-app path as the absolute URL a chip's `href` stores: the server keeps only http(s) and mailto links, so a
 * relative one would be dropped. Anything not starting with `/` is returned as it is.
 */
export const appPageUrl = (path) =>
  path?.startsWith('/') ? `${globalThis.K?.config?.baseUrl || globalThis.location?.origin || ''}${path}` : path;

/**
 * The `referencedUsers` (`{ usr_x: true }`) and `referencedUsersAll` a chat message is saved with: its user
 * anchors, `data-id="all"` being @All. The chat server stores what it is sent and reads no anchors itself.
 */
export const mentionedUsers = (html) => {
  const ids = html
    ? [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('a[data-type="user"][data-id]')].map((anchor) => anchor.getAttribute('data-id'))
    : [];
  return {
    referencedUsers: Object.fromEntries(ids.filter((id) => id.startsWith('usr_')).map((id) => [id, true])),
    referencedUsersAll: ids.includes('all'),
  };
};

/** A chip's label: its title, name or person's name, else the text written in it, else its id. */
export const chipLabel = (details, ownText, id) =>
  details?.title || details?.name || [details?.firstName, details?.lastName].filter(Boolean).join(' ') || ownText || id || '';

/**
 * The in-app page a reference opens, or null: a card or board page; a sub-task's or file's card
 * (`?tab=tasks` / `?tab=attachments`); a board file's and a tag's board. A user and an AI Helper
 * upload (`aca_`) have none.
 *
 * @param {Object} reference - `{ type, id, details }`
 * @param {Object} [fallback] - `{ accountId, boardId }` when the details do not name them
 */
export const referencePagePath = ({ type, id, details = {} } = {}, { accountId, boardId } = {}) => {
  const account = details.board?.accountId || details.accountId || accountId;
  if (!account || !id) return null;
  const board = details.board?.id || boardId;
  const boardPage = (pageBoardId) => (pageBoardId ? `/${account}/board/${pageBoardId}` : null);
  const cardPage = (card, tab) => (card && board ? `${boardPage(board)}/${card}${tab ? `?tab=${tab}` : ''}` : null);
  switch (type) {
    case 'CARD':
      return cardPage(id);
    case 'BOARD':
      return boardPage(id);
    case 'SUBTASK':
      return cardPage(details.card?.id, 'tasks');
    case 'FILE':
      return id.startsWith('aca_') ? null : cardPage(details.card?.id, 'attachments') || boardPage(board);
    case 'TAG':
      return boardPage(board);
    case 'AICHAT': {
      if (!board) return null;
      return details.card?.id ? `${boardPage(board)}/${details.card.id}?card-aichat=${id}` : `${boardPage(board)}?aichat=${id}`;
    }
    default:
      return null;
  }
};

/**
 * The href a reference stores. A file opens its own URL (a Google Doc's, say); without one, its preview on the page it
 * lives on (`file-preview`), which is null for an AI Helper upload, as no preview route exists for one yet. Any other
 * reference opens its page from `referencePagePath`. All are absolute URLs.
 */
export const referenceHref = ({ type, id, details = {} } = {}, fallback) => {
  if (type === 'FILE') {
    if (details.url) return details.url;
    const page = referencePagePath({ type, id, details }, fallback);
    return page ? appPageUrl(`${page}${page.includes('?') ? '&' : '?'}file-preview=${id}`) : null;
  }
  const path = referencePagePath({ type, id, details }, fallback);
  return path ? appPageUrl(path) : null;
};

/**
 * A prompt's chips as the plain anchors the server stores and the AI reads:
 * `<a href="<absolute page url>" data-type="card|board|file|subtask|tag|user|aichat" data-id="<id>">Title</a>`,
 * linked to `referencePagePath`. No `details` JSON is kept. The title is the chip's text, set as
 * text, never HTML.
 *
 * @param {String} html - Prompt HTML as the editor holds it
 * @param {Object} [fallback] - `referencePagePath`'s, for chips whose details do not name the account or board
 * @returns {String} the same HTML with each chip replaced by its anchor
 */
export const chipsToAnchors = (html, fallback) => {
  if (!html || !/chip-item/.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll(CHIP_SELECTOR).forEach((chip) => {
    const anchor = chipToAnchor(chip, fallback);
    anchor && chip.replaceWith(anchor);
  });
  return doc.body.innerHTML;
};

/**
 * One chip element as `chipsToAnchors`' anchor, or null when it names no reference. A live chip gives its `details`
 * and `titleText`; a parsed one its `details` attribute and text.
 */
export const chipToAnchor = (chip, fallback) => {
  const id = chip.getAttribute('item-id');
  const type = chipReferenceType(chip);
  if (!id || !type) return null;
  let details = chip.details;
  try {
    details ||= JSON.parse(chip.getAttribute('details') || '{}') || {};
  } catch {
    // A chip without readable details still keeps its type, id and text.
    details = {};
  }
  const anchor = chip.ownerDocument.createElement('a');
  const href = referenceHref({ type, id, details }, fallback);
  if (href) anchor.setAttribute('href', href);
  anchor.setAttribute('data-type', type.toLowerCase());
  anchor.setAttribute('data-id', id);
  if (type === 'FILE' && !id.startsWith('aca_') && details.board?.id) {
    anchor.setAttribute('data-board-id', details.board.id);
    details.card?.id && anchor.setAttribute('data-card-id', details.card.id);
  }
  anchor.textContent = chip.titleText || chip.textContent.trim() || chipLabel(details, '', id);
  return anchor;
};


/**
 * Every index where `url` appears in `text` as a whole URL — not as the start of a longer one, so a
 * board URL does not match inside the card URL that extends it. Sentence punctuation may follow.
 */
export const wholeUrlIndexes = (text, url) => {
  const indexes = [];
  if (!text || !url) return indexes;
  for (let i = text.indexOf(url); i !== -1; i = text.indexOf(url, i + 1)) {
    if (/^[.,;:!?)\]}]*(?:$|[\s<>"'])/.test(text.slice(i + url.length))) {
      indexes.push(i);
    }
  }
  return indexes;
};

/**
 * Reads a Kerika board or card link, e.g.
 *   /{accountId}/board/{brd_x}            → BOARD
 *   /{accountId}/board/{brd_x}/{crd_y}    → CARD
 *   …/{brd_x}?aichat={aic_z}, …/{crd_y}?card-aichat={aic_z} → AICHAT (`aichat=new` is not a chat)
 *
 * Only the path is inspected — the caller has already established that the URL is ours. The board
 * and account ids travel with the result so a chip can be built (and later navigated) without a
 * second lookup; only the title has to come from the server.
 *
 * @param {String} url - Absolute URL
 * @returns {Object|null} `{ url, type, id, boardId, accountId }` (an AI chat also `cardId`), or null
 *   when it is not one
 */
export const parseReferenceLink = (url) => {
  let pathname, searchParams;
  try {
    ({ pathname, searchParams } = new URL(url));
  } catch {
    return null;
  }

  const [accountId, section, boardId, cardId] = pathname.split('/').filter(Boolean);
  if (section !== 'board' || !boardId?.startsWith('brd_')) return null;

  const isCard = cardId?.startsWith('crd_');
  const isChatId = (id) => /^aic_[A-Za-z0-9]+$/.test(id || '');
  const cardChatId = isCard && searchParams.get('card-aichat');
  if (isChatId(cardChatId)) {
    return { url, type: 'AICHAT', id: cardChatId, boardId, accountId, cardId };
  }
  if (isChatId(searchParams.get('aichat'))) {
    return { url, type: 'AICHAT', id: searchParams.get('aichat'), boardId, accountId };
  }
  const fileId = searchParams.get('file-preview');
  if (fileId) {
    return { url, type: 'FILE', id: fileId, boardId, accountId, cardId: isCard ? cardId : undefined };
  }

  return isCard
    ? { url, type: 'CARD', id: cardId, boardId, accountId }
    : { url, type: 'BOARD', id: boardId, boardId, accountId };
};

/**
 * Every link in pasted content worth resolving, de-duplicated by URL so one call covers each link
 * once however many times it was pasted.
 *
 * Two kinds come back. A Kerika link carries its own `crd_`/`brd_` id and resolves deterministically.
 * Any other URL is a candidate **attachment**: a cloud document link carries no Kerika id, so it can
 * only be found by matching an attachment's stored `url`, and the same is true of a plain weblink
 * attachment. Most such URLs are attached nowhere and resolve to nothing, which is why an
 * unresolved link has to be a silent no-op rather than a failure.
 *
 * `isOurUrl` is passed in rather than imported so this stays free of redux/router — the caller
 * hands it `router.actions.isAppUrl`.
 *
 * @param {String} text - Pasted text (plain or HTML; both are scanned as raw text)
 * @param {Function} isOurUrl - Predicate telling a Kerika URL from any other
 * @returns {Array} Parsed links, in the order they appear
 */
export const parseReferenceLinks = (text, isOurUrl) => {
  if (!text) return [];

  return [...new Set(text.match(URL_PATTERN) || [])].flatMap((url) => {
    // Someone else's URL cannot carry a Kerika id — look it up by url instead.
    if (!isOurUrl(url)) return [{ url, type: 'FILE' }];
    // Ours, but not a board or card page (an account or settings URL, say) — nothing to resolve.
    return parseReferenceLink(url) || [];
  });
};

/**
 * What may come right before a mention's `@`: whitespace or an opening bracket, as in `(@Name)`. Never a letter, so
 * an email address's `@` is no mention.
 */
export const MENTION_PREFIX = /[\s([{]/;

/**
 * Where the `@` mention that ends `text` starts: the last `@` at the start or after `MENTION_PREFIX`, or -1. `text`
 * is the line up to the caret, with each chip read as a space.
 */
export const mentionStartIndex = (text) => {
  for (let i = (text || '').length - 1; i >= 0; i--) {
    if (text[i] === '@' && (i === 0 || MENTION_PREFIX.test(text[i - 1]))) {
      return i;
    }
  }
  return -1;
};

/**
 * The mentions in pasted text: `@` at the start or after `MENTION_PREFIX`, then a member's full name (the longest
 * that fits, ending at a word boundary, any case), `usr_…`, or `All` when `withAll`. A name two members share
 * is left as text, so the wrong person is never mentioned.
 *
 * @param {String} text
 * @param {Array} members - `{ id, name }`
 * @param {Object} [options] - `withAll`: `@All` matches too
 * @returns {Array} `{ index, length, id, member }`, `member` undefined for `usr_…` not among `members` and for `all`
 */
export const matchPastedMentions = (text, members = [], { withAll = false } = {}) => {
  const named = members.filter((member) => member.name).sort((a, b) => b.name.length - a.name.length);
  const lower = (text || '').toLowerCase();
  const endsAt = (end) => !/[\p{L}\p{N}_]/u.test(text[end] || '');
  const matches = [];
  for (let i = 0; i < lower.length; i++) {
    if (lower[i] !== '@' || (i > 0 && !MENTION_PREFIX.test(text[i - 1]))) {
      continue;
    }
    const start = i + 1;
    const name = named.find((member) => lower.startsWith(member.name.toLowerCase(), start) && endsAt(start + member.name.length))?.name.toLowerCase();
    const sameName = name ? named.filter((member) => member.name.toLowerCase() === name) : [];
    const userId = text.slice(start).match(/^usr_[A-Za-z0-9]+\b/)?.[0];
    let match = null;
    if (sameName.length === 1) {
      match = { length: name.length + 1, id: sameName[0].id, member: sameName[0] };
    } else if (!name && userId) {
      match = { length: userId.length + 1, id: userId, member: members.find((member) => member.id === userId) };
    } else if (!name && withAll && lower.startsWith('all', start) && endsAt(start + 3)) {
      match = { length: 4, id: 'all' };
    }
    if (match) {
      matches.push({ index: i, ...match });
      i += match.length - 1;
    }
  }
  return matches;
};

/** `toEditorValue`'s default `isOurUrl`: a production Kerika URL, for the migration script, which has no app URL. */
const KERIKA_URL = /^https?:\/\/([a-z0-9-]+\.)*kerika\.(com|net)(:\d+)?\//i;
const isKerikaUrl = (url) => KERIKA_URL.test(url);

/** What old content turns into: `` `keyword` ``, a URL, `@usr_…` or `@All`. */
const OLD_CONTENT_TOKEN = /`([^`\n]+?)`|(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]}])|\B@(usr_[A-Za-z0-9]+)\b|\B@(All)\b/gi;

/**
 * Squire wrote lines as blocks, so content with a top-level block is HTML, whatever else sits beside it (a
 * `<script>`, loose text). Without one it is plain text, where a typed `<b>` is literal.
 */
const SQUIRE_BLOCK = /^(DIV|P|UL|OL|H[1-6]|BLOCKQUOTE|PRE|TABLE|HR)$/;

const _isSquireHtml = (body) => [...body.children].some((node) => SQUIRE_BLOCK.test(node.nodeName));

const _tokenNode = (doc, match, { titleOf, isOurUrl, oldLinks }) => {
  const [, keyword, url, userId, all] = match;
  const element = (tag, attributes, text) => {
    const node = doc.createElement(tag);
    Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, value));
    node.textContent = text;
    return node;
  };
  if (keyword !== undefined) {
    return element('code', {}, keyword);
  }
  if (userId) {
    return element('a', { 'data-type': 'user', 'data-id': userId }, titleOf({ type: 'USER', id: userId }) || userId);
  }
  if (all) {
    return element('a', { 'data-type': 'user', 'data-id': 'all' }, 'All');
  }
  // A Kerika URL stays a plain link with its URL as text, as the old app showed it; only new content has chips.
  return element('a', { href: url }, isOurUrl(url) ? url : oldLinks.get(url)?.text || url);
};

/** The links in the server's old HTML (`htmlMessage`, `description2`): URL → `{ text }`. */
const _oldLinks = (oldHtml, Parser) => {
  const links = new Map();
  if (oldHtml) {
    new Parser().parseFromString(oldHtml, 'text/html').querySelectorAll('a[href^="http"]').forEach((a) => {
      links.has(a.getAttribute('href')) || links.set(a.getAttribute('href'), { text: a.textContent.trim() });
    });
  }
  return links;
};

/** Turns the tokens in `body`'s text into keywords, links and anchors; text already in a link or code is left. */
const _convertOldTokens = (body, options) => {
  const doc = body.ownerDocument;
  const walker = doc.createTreeWalker(body, 4);
  const textNodes = [];
  while (walker.nextNode()) {
    if (!walker.currentNode.parentElement.closest('a, code, pre')) {
      textNodes.push(walker.currentNode);
    }
  }
  textNodes.forEach((textNode) => {
    const text = textNode.textContent;
    const nodes = [];
    let last = 0;
    for (const match of text.matchAll(OLD_CONTENT_TOKEN)) {
      nodes.push(doc.createTextNode(text.slice(last, match.index)), _tokenNode(doc, match, options));
      last = match.index + match[0].length;
    }
    if (nodes.length) {
      textNode.replaceWith(...nodes, doc.createTextNode(text.slice(last)));
    }
  });
};

/**
 * What a rich text editor loads: `descriptionHtml` as it is (`''` too: a cleared description), else the old
 * `description` (Squire HTML or plain text) converted. Chat passes `{ descriptionHtml: messageHtml, description: message }`.
 *
 * Plain JS with no Redux or browser global, so the stage-2 migration script runs it in Node.js with its own parser.
 *
 * @param {Object} content - `{ descriptionHtml, description }`
 * @param {Object} [options]
 * @param {Function} [options.DOMParser] - The HTML parser class; the browser's by default
 * @param {Function} [options.titleOf] - `({ type, id })` → a user's name
 * @param {Function} [options.isOurUrl] - Whether a URL is the app's own, so it is left for a chip once its doc is read;
 *   the app passes `router.actions.isAppUrl`, the default knows only the production domains
 * @param {String} [options.oldHtml] - The server's HTML of the old content (`description2`, `htmlMessage`): its web
 *   link titles are kept
 * @returns {String} HTML
 */
export const toEditorValue = (
  { descriptionHtml, description } = {},
  { DOMParser: Parser = globalThis.DOMParser, titleOf = () => undefined, isOurUrl = isKerikaUrl, oldHtml } = {}
) => {
  if (descriptionHtml != null) {
    return descriptionHtml;
  }
  if (!description) {
    return '';
  }
  const body = new Parser().parseFromString(description, 'text/html').body;
  if (!_isSquireHtml(body)) {
    body.replaceChildren(
      ...description.split('\n').map((line) => {
        const div = body.ownerDocument.createElement('div');
        div.append(line || body.ownerDocument.createElement('br'));
        return div;
      })
    );
  }
  _convertOldTokens(body, { titleOf, isOurUrl, oldLinks: _oldLinks(oldHtml, Parser) });
  return body.innerHTML;
};

/**
 * A saved draft as the editor loads it: the editor's own HTML as it is, so a typed `@All` or backtick stays text;
 * an old plain-text draft is converted like an old description.
 *
 * @param {String} draft
 * @param {Object} [options] - As for `toEditorValue`
 * @returns {String} HTML
 */
export const draftToEditorValue = (draft, options = {}) => {
  const Parser = options.DOMParser || globalThis.DOMParser;
  return draft && _isSquireHtml(new Parser().parseFromString(draft, 'text/html').body)
    ? draft
    : toEditorValue({ description: draft }, options);
};

const PLAIN_TEXT_BLOCKS = 'div, p, li, h1, h2, h3, h4, h5, h6, blockquote, pre, table, tr, ul, ol';

/** Blocks set apart by a blank line, as a browser copies them; a line (`div`) and a list item are not. */
const PLAIN_TEXT_PARAGRAPHS = 'p, h1, h2, h3, h4, h5, h6, blockquote, pre, table, ul, ol';

/**
 * The editor's HTML as plain text, for the UI's own clipboard text only; never saved. A line per `div`, a blank
 * line around paragraphs and lists, and list items marked `- ` or `1. `, nested ones indented. A table row is its
 * cells joined by a tab; a link is "text (URL)" unless its text is the URL, a chip "Title (URL)".
 *
 * @param {String} html
 * @param {Object} [options] - `DOMParser`: the HTML parser class; the browser's by default
 * @returns {String}
 */
export const toPlainText = (html, { DOMParser: Parser = globalThis.DOMParser } = {}) => {
  const body = new Parser().parseFromString(html || '', 'text/html').body;
  // Line breaks between tags (marked writes them) are markup, not text.
  const walker = body.ownerDocument.createTreeWalker(body, 4);
  const markupBreaks = [];
  while (walker.nextNode()) {
    /^\s*\n\s*$/.test(walker.currentNode.data) && markupBreaks.push(walker.currentNode);
  }
  markupBreaks.forEach((node) => node.remove());
  body.querySelectorAll('a[href]').forEach((anchor) => {
    const url = appPageUrl(anchor.getAttribute('href'));
    const bare = (text) => text.replace(/^[a-z][\w+.-]*:(\/\/)?/i, '').replace(/\/$/, '');
    anchor.textContent && bare(anchor.textContent) !== bare(url) && anchor.append(` (${url})`);
  });
  body.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  body.querySelectorAll('td, th').forEach((cell) => {
    cell.querySelectorAll(PLAIN_TEXT_BLOCKS).forEach((block) => block.append('\n'));
    cell.textContent = cell.textContent.trim().replace(/\s*\n\s*/g, ' ');
    cell.nextElementSibling && cell.append('\t');
  });
  body.querySelectorAll('li').forEach((li) => {
    const list = li.parentElement;
    let depth = 0;
    for (let item = list.closest('li'); item; item = item.parentElement.closest('li')) depth++;
    const items = [...list.children].filter((el) => el.tagName === 'LI');
    const marker = list.tagName === 'OL' ? `${(Number(list.getAttribute('start')) || 1) + items.indexOf(li)}. ` : '- ';
    li.prepend('  '.repeat(depth) + marker);
  });
  // Innermost first, so a block holding a list that already ends its line adds no second one.
  [...body.querySelectorAll(PLAIN_TEXT_BLOCKS)].reverse().forEach((block) => {
    const startsLine = block.matches('ul, ol') && block.parentElement.closest('li') && !block.previousSibling?.matches?.(PLAIN_TEXT_BLOCKS);
    startsLine && block.before('\n');
    block.textContent.endsWith('\n') || block.append('\n');
    block.matches(PLAIN_TEXT_PARAGRAPHS) && !block.parentElement.closest('li') && (block.before('\n'), block.after('\n'));
  });
  return body.textContent.replace(/\n{3,}/g, '\n\n').trim();
};
